import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigestPreviewDialog({
  open,
  onClose,
  email = "",
  showSubscribe = false,
  onSubscribe,
  subscribing = false,
  testIdPrefix = "digest-preview",
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Fetch preview HTML when dialog opens. We do NOT reset `preview` on close
  // so closing+reopening immediately shows the cached HTML; the parent can
  // remount the component if a fresh fetch is needed.
  useEffect(() => {
    if (!open || preview || loading) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/digest/preview`, { withCredentials: true });
        if (!cancelled) setPreview(r.data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || t("digest.could_not_load"));
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, preview, loading, onClose, t]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base">{t("digest.preview_title")}</DialogTitle>
          <p className="text-[11px] text-gray-500 font-body">
            {email
              ? t("digest.preview_desc_named", { email })
              : t("digest.preview_desc_self")}
            {t("digest.preview_attachments")}
          </p>
        </DialogHeader>
        <div className="mt-2">
          {loading || !preview ? (
            <div className="flex items-center justify-center py-12 text-gray-400" data-testid={`${testIdPrefix}-loading`}>
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("digest.preview_loading")}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px] font-body text-gray-600">
                <span className="px-2 py-0.5 rounded-sm bg-gray-100" data-testid={`${testIdPrefix}-alerts`}>{t("digest.alerts_count", { n: preview.alerts })}</span>
                <span className="px-2 py-0.5 rounded-sm bg-gray-100" data-testid={`${testIdPrefix}-sanctions`}>{t("digest.sanctions_count", { n: preview.sanctions })}</span>
                <span className="px-2 py-0.5 rounded-sm bg-gray-100" data-testid={`${testIdPrefix}-scans`}>{t("digest.scans_count", { n: preview.scans })}</span>
                <span className="text-gray-400 ml-1">
                  {new Date(preview.since).toLocaleDateString(undefined, { day: "numeric", month: "short" })} → {new Date(preview.until).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                </span>
              </div>
              <iframe
                title="Digest preview"
                srcDoc={`<!doctype html><html><body style="margin:0;padding:16px;background:#fff;">${preview.html}</body></html>`}
                sandbox=""
                className="w-full h-[420px] border border-gray-200 rounded-sm bg-white"
                data-testid={`${testIdPrefix}-iframe`}
              />
            </>
          )}
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-body text-gray-600 hover:text-gray-900 px-3 py-1.5"
            data-testid={`${testIdPrefix}-close`}
          >
            {t("common.close")}
          </button>
          {showSubscribe && (
            <button
              type="button"
              onClick={onSubscribe}
              disabled={subscribing}
              data-testid={`${testIdPrefix}-subscribe`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold font-body bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            >
              {subscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("digest.subscribe_weekly")}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
